if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        this.submitButton = this.querySelector('[type="submit"][name="add"]');
        this.submitButtonText = this.submitButton?.querySelector('span');

        this.payButton = this.querySelector('[data-stripe-pay-button]');

        if (document.querySelector('cart-drawer') && this.submitButton) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';

        if (this.payButton) {
          this.payButton.addEventListener(
            'click',
            this.onStripePayHandler.bind(this)
          );
        }
      }

      getSelectedSellingPlan() {
        const formData = new FormData(this.form);

        return (
          formData.get('selling_plan') ||
          new URLSearchParams(window.location.search).get('selling_plan') ||
          ''
        );
      }

      async onStripePayHandler(evt) {
        evt.preventDefault();

        if (this.payButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.payButton.setAttribute('aria-disabled', true);
        this.payButton.classList.add('loading');

        const spinner = this.payButton.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        try {
          await this.handleStripeCheckout();
        } catch (error) {
          console.error(error);
          this.handleErrorMessage(
            error?.message || 'Something went wrong. Please try again.'
          );
        } finally {
          this.payButton.classList.remove('loading');
          this.payButton.removeAttribute('aria-disabled');

          if (spinner) spinner.classList.add('hidden');
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton?.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });

              this.handleErrorMessage(response.description);

              const soldOutMessage =
                this.submitButton.querySelector('.sold-out-message');

              if (!soldOutMessage) return;

              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText?.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker =
              CartPerformance.createStartingMarker('add:wait-for-subscribers');

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker(
                  'add:wait-for-subscribers',
                  startMarker
                );
              });
            }

            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');

            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');

            if (this.cart && this.cart.classList.contains('is-empty')) {
              this.cart.classList.remove('is-empty');
            }

            if (!this.error) {
              this.submitButton.removeAttribute('aria-disabled');
            }

            this.querySelector('.loading__spinner')?.classList.add('hidden');
            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      async handleStripeCheckout() {
        const formData = new FormData(this.form);

        const selectedSellingPlan = this.getSelectedSellingPlan();
        const isSubscriptionSelected = !!selectedSellingPlan;

        const endpoint = isSubscriptionSelected
          ? this.dataset.subscriptionCheckoutUrl
          : this.dataset.oneTimeCheckoutUrl;

        if (!endpoint) {
          throw new Error(
            isSubscriptionSelected
              ? 'Subscription checkout URL is missing.'
              : 'One-time checkout URL is missing.'
          );
        }

        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            productId: formData.get('id'),
            sellingPlan: selectedSellingPlan || null,
            mode: isSubscriptionSelected ? 'subscription' : 'payment',
            source: 'product-page',
            productUrl: window.location.href,
          }),
        });

        const result = await response.json();

        if (!response.ok || !result?.success) {
          throw new Error(
            result?.message || 'Failed to create checkout session'
          );
        }

        const checkoutUrl = result?.data?.url;

        if (!checkoutUrl) {
          throw new Error('Invalid checkout URL received');
        }

        window.location.href = checkoutUrl;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector('.product-form__error-message-wrapper');

        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;

        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && this.submitButtonText) {
            this.submitButtonText.textContent = text;
          }
        } else {
          this.submitButton.removeAttribute('disabled');
          if (this.submitButtonText) {
            this.submitButtonText.textContent = window.variantStrings.addToCart;
          }
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}